import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { ParticipantLayout } from "../../components/Layout";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  GraduationCap, BookOpen, Sparkles, Wrench, TrendingUp, ArrowRight,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Tile = ({ to, icon: Icon, title, description, color, dataTestId }) => (
  <Link to={to} className="group block" data-testid={dataTestId}>
    <Card className="bg-card border-border hover:border-primary/60 transition-all hover:-translate-y-0.5">
      <CardContent className="p-6">
        <div className={`w-12 h-12 rounded-sm flex items-center justify-center mb-4 ${color || "bg-primary/15 text-primary"}`}>
          <Icon className="h-6 w-6" />
        </div>
        <p className="text-lg font-semibold mb-1">{title}</p>
        <p className="text-sm text-muted-foreground line-clamp-2 min-h-[40px]">{description}</p>
        <div className="flex items-center gap-1 text-xs text-primary mt-3 group-hover:gap-2 transition-all">
          Öffnen <ArrowRight className="h-3 w-3" />
        </div>
      </CardContent>
    </Card>
  </Link>
);

const Lernportal = () => {
  const { user } = useAuth();
  const [progress, setProgress] = useState({ flashcards_studied: 0, courses: 0, messages_unread: 0 });

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    try {
      const [unreadRes] = await Promise.all([
        axios.get(`${API}/messages/unread-count`).catch(() => ({ data: { unread_count: 0 } })),
      ]);
      setProgress((p) => ({ ...p, messages_unread: unreadRes.data?.unread_count || 0 }));
    } catch {
      // silent
    }
  };

  return (
    <ParticipantLayout title="Lernportal">
      <div className="space-y-6" data-testid="lernportal-page">
        {/* Welcome Hero */}
        <Card className="bg-gradient-to-br from-primary/15 via-primary/5 to-transparent border-primary/30">
          <CardContent className="p-6 sm:p-8">
            <Badge variant="outline" className="mb-3 text-xs">Industriemeister Metall</Badge>
            <h2 className="text-2xl sm:text-3xl font-semibold mb-2">
              Willkommen, {user?.name?.split(" ")[0] || "Lerner"}!
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground max-w-2xl">
              Dein Lernportal für die Prüfung zum Industriemeister Metall. Lerne mit Karteikarten,
              stelle Fragen an den Meister-Agenten und verfolge deinen Fortschritt.
            </p>
          </CardContent>
        </Card>

        {/* Tiles Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Tile
            to="/lernkarten"
            icon={BookOpen}
            title="Lernkarten"
            description="Übe gezielt mit Karteikarten – aufgeteilt nach allen BQ- und HQ-Fächern."
            dataTestId="tile-lernkarten"
          />
          <Tile
            to="/meister-agent"
            icon={Sparkles}
            title="S&T Meister-Agent"
            description="KI-Tutor mit Erklärungen, Formeln (MathJax), Skizzen (SVG) und ELI14-Variante."
            color="bg-yellow-500/15 text-yellow-500"
            dataTestId="tile-meister-agent"
          />
          <Tile
            to="/meister-agent?bereich=bq"
            icon={GraduationCap}
            title="BQ"
            description="Basisqualifikation: NTG, BWH, RBH, MIKP, ZIB"
            color="bg-blue-500/15 text-blue-500"
            dataTestId="tile-bq"
          />
          <Tile
            to="/meister-agent?bereich=hq"
            icon={Wrench}
            title="HQ"
            description="Handlungsspezifisch: Fertigung, Montage, Betrieb, Kostenwesen"
            color="bg-green-500/15 text-green-500"
            dataTestId="tile-hq"
          />
          <Tile
            to="/dashboard"
            icon={TrendingUp}
            title="Meine Fortschritte"
            description={`Persönliche Übersicht über deine Buchungen, Termine und ungelesene Nachrichten (${progress.messages_unread}).`}
            color="bg-purple-500/15 text-purple-500"
            dataTestId="tile-fortschritte"
          />
          <Tile
            to="/bewerbung"
            icon={GraduationCap}
            title="Bewerbungs-Tool"
            description="Bewerbung & Lebenslauf mit KI – sofort als DIN A4 PDF."
            color="bg-pink-500/15 text-pink-500"
            dataTestId="tile-bewerbung"
          />
        </div>

        {/* Tip */}
        <Card className="bg-card border-border">
          <CardContent className="p-4 sm:p-5 flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium mb-1">Tipp des Tages</p>
              <p className="text-sm text-muted-foreground">
                Der Meister-Agent kann Aufgaben Schritt für Schritt durchrechnen –
                inkl. Formel, Beispiel, Rechenweg, Lösung, ELI14-Erklärung und typischem Prüfungsfehler.
                Probier es einfach mit deiner nächsten Übungsaufgabe aus!
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </ParticipantLayout>
  );
};

export default Lernportal;
